import {
  CreateOrganizationBody,
  Organization,
  OrganizationNodeProps,
  UpdateOrganizationBody,
} from '@ixicare/resources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const ORGANIZATIONS_QUERY_KEY = ['organizations'];

export const useOrganizations = () =>
  useQuery({
    queryKey: ORGANIZATIONS_QUERY_KEY,
    queryFn: () => Organization.getOrganizations(),
  });

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrganizationBody) => Organization.createOrganization(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY }),
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOrganizationBody }) =>
      Organization.updateOrganization(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY }),
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => Organization.deleteOrganization(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY }),
  });
};

export const flattenOrganizations = (
  nodes: OrganizationNodeProps[],
  depth = 0
): { node: OrganizationNodeProps; depth: number }[] => {
  return nodes.flatMap((node) => [{ node, depth }, ...flattenOrganizations(node.children, depth + 1)]);
};

export const findOrganizationById = (nodes: OrganizationNodeProps[], id: string): OrganizationNodeProps | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findOrganizationById(node.children, id);
    if (found) return found;
  }
  return null;
};

export const collectDescendantIds = (node: OrganizationNodeProps): Set<string> => {
  const ids = new Set<string>([node.id]);
  for (const child of node.children) {
    for (const id of collectDescendantIds(child)) ids.add(id);
  }
  return ids;
};

export const findOrganizationOfSite = (
  nodes: OrganizationNodeProps[],
  siteUuid: string
): OrganizationNodeProps | null => {
  for (const node of nodes) {
    if (node.sites.some((s) => s.uuid === siteUuid)) return node;
    const found = findOrganizationOfSite(node.children, siteUuid);
    if (found) return found;
  }
  return null;
};
